import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PrimaryButton, Card, ScreenHeader } from '../ui';
import gamesService from '../../../services/gamesService';
import { useGameEligibility } from '../useGameEligibility';
import GameLockedCard from '../GameLockedCard';
import { useScratchCanvas, drawScratchSurface } from '../../../hooks/useScratchCanvas';

interface ScratchCardGameProps {
  appName: string;
  outletId: number;
  onClose?: () => void;
}

const ScratchCardGame: React.FC<ScratchCardGameProps> = ({ appName, outletId, onClose }) => {
  const { loading, eligible, eligibility } = useGameEligibility(appName, 'SCRATCH_CARD');
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [resultLabel, setResultLabel] = useState<string | null>(null);
  const [redemptionCode, setRedemptionCode] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [scratchPct, setScratchPct] = useState(0);
  const spinRequestedRef = useRef(false);

  const { handlers } = useScratchCanvas(canvasRef, {
    onRevealed: useCallback(() => setRevealed(true), []),
    revealThreshold: 55,
  });

  useEffect(() => {
    if (!eligible || spinRequestedRef.current) return;
    spinRequestedRef.current = true;
    const key = gamesService.dailyIdempotencyKey(outletId, 'SCRATCH_CARD');
    gamesService
      .spin(appName, 'SCRATCH_CARD', key)
      .then((res) => {
        setResultLabel(res.resultSegment);
        setRedemptionCode(res.redemptionCode);
      })
      .catch(() => {
        setResultLabel('Come back tomorrow for another scratch card.');
      });
  }, [appName, outletId, eligible]);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !resultLabel) return;

    function resizeAndDraw() {
      const width = container!.clientWidth;
      const height = container!.clientHeight;
      if (width > 0 && height > 0) {
        drawScratchSurface(canvas!, width, height);
      }
    }

    resizeAndDraw();
    const observer = new ResizeObserver(resizeAndDraw);
    observer.observe(container);
    return () => observer.disconnect();
  }, [resultLabel]);

  const locked = !loading && !eligible;
  const canvasHandlers = handlers(setScratchPct);

  return (
    <div style={{ background: 'var(--bg, #FFFFFF)', minHeight: '100vh', paddingBottom: 24 }}>
      <ScreenHeader
        title={<>Scratch &amp; <span style={{ color: '#D9472B' }}>Win</span></>}
        subtitle="Use your finger to scratch the panel"
        onClose={onClose}
      />
      <div style={{ padding: '18px 24px 0' }}>
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted, #9B8F6E)', marginTop: 40 }}>Loading…</p>
        )}

        {locked && <GameLockedCard eligibility={eligibility} />}

        {!loading && eligible && (
          <>
            <div
              ref={containerRef}
              style={{
                position: 'relative',
                width: '100%',
                height: 180,
                borderRadius: 14,
                overflow: 'hidden',
                border: '1px solid var(--border-color, #E5E2D8)',
                background: '#FFFFFF',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {resultLabel ? (
                  <>
                    <div style={{ fontWeight: 800, fontSize: 22, color: 'var(--primary-dark, #D4A416)' }}>
                      {resultLabel}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-secondary, #6B5E3E)', marginTop: 4 }}>
                      on today's bill
                    </div>
                  </>
                ) : (
                  <span style={{ color: 'var(--text-muted, #9B8F6E)', fontSize: 12 }}>Loading your card…</span>
                )}
              </div>
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  touchAction: 'none',
                  cursor: revealed ? 'default' : 'pointer',
                }}
                {...canvasHandlers}
              />
            </div>

            <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--text-muted, #9B8F6E)', marginTop: 8 }}>
              {scratchPct < 55 ? `${scratchPct}% scratched - keep going!` : 'Fully revealed!'}
            </p>

            <div style={{ height: 14 }} />
            {revealed && redemptionCode && (
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted, #9B8F6E)' }}>REDEMPTION CODE</div>
                <div style={{ fontWeight: 700, fontSize: 18, letterSpacing: 2, marginTop: 2 }}>{redemptionCode}</div>
              </Card>
            )}
            <div style={{ height: 14 }} />
            <PrimaryButton disabled={!revealed || !redemptionCode}>
              {revealed ? 'CLAIM REWARD' : 'SCRATCH TO REVEAL'}
            </PrimaryButton>
            <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted, #9B8F6E)', marginTop: 10 }}>
              One scratch card per customer / day
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ScratchCardGame;
